/**
 * WebSocket service for ThingsBoard real-time telemetry updates
 */
class ThingsBoardWebSocket {
    constructor() {
      this.ws = null;
      this.reconnectAttempt = 0;
      this.subscriptions = new Map();
      this.onMessageCallbacks = [];
      this.isConnecting = false;
      // What we want to be subscribed to, independent of the current socket.
      // Replayed after every reconnect -- without this a reconnected socket is
      // open and silent, which looks healthy and delivers nothing.
      this.desired = null;
      this.started = false;
      // ThingsBoard's cmdId is a Java int. Date.now() overflows it and the
      // whole command is rejected with {"errorCode":2,"errorMsg":"Failed to
      // parse the payload"}; 2147483647 is accepted and 2147483648 is not.
      this.nextCmdId = 1;
    }

    newCmdId() {
      if (this.nextCmdId >= 2147483647) this.nextCmdId = 1;
      return this.nextCmdId++;
    }

    /**
     * The proxy sends {type:'ping'} every 20s. If nothing at all arrives for
     * three beats the socket is dead however healthy readyState looks, so drop
     * it and let onclose reconnect. Without this a half-open connection reports
     * itself connected forever and quietly delivers nothing.
     */
    startStaleWatch() {
      this.stopStaleWatch();
      this.lastMessageMs = Date.now();
      this.staleTimer = setInterval(() => {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
        if (Date.now() - this.lastMessageMs > 65000) {
          console.warn('💀 No traffic from the proxy for 65s, treating the socket as dead');
          try { this.ws.close(4000, 'stale'); } catch { /* onclose will reconnect */ }
        }
      }, 10000);
    }

    stopStaleWatch() {
      if (this.staleTimer) clearInterval(this.staleTimer);
      this.staleTimer = null;
    }
  
    /**
     * Connect through the backend WebSocket proxy.
     *
     * The proxy (server/services/websocket-proxy.js, path /api/ws) holds the
     * ThingsBoard credentials and authenticates upstream on our behalf, so the
     * browser presents only its own app JWT and never handles a ThingsBoard
     * token. It announces readiness with {type:'ready'}; subscriptions sent
     * before that are dropped, so we wait for it.
     */
    async connect(deviceId, keys) {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        console.log('WebSocket already connected');
        return;
      }

      if (this.isConnecting) {
        console.log('WebSocket connection already in progress');
        return;
      }

      this.isConnecting = true;
      this.started = true;
      // Remember the intent, so a reconnect re-subscribes to the same thing.
      this.desired = { deviceId, keys };

      const token = localStorage.getItem('token');
      const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      // The token goes in the opening frame, not the URL. nginx logs the full
      // request line, so a query-string token writes the operator's JWT to disk
      // in cleartext on every connect -- and reconnects are unlimited.
      const url = `${scheme}//${window.location.host}/api/ws`;
      console.log('🔌 Connecting to dashboard WebSocket proxy:', url);

      return new Promise((resolve, reject) => {
        this.ws = new WebSocket(url);

        let handshakeTimeout;
        const handshake = (event) => {
          let data;
          try {
            data = JSON.parse(event.data);
          } catch {
            return;
          }
          if (data.errorCode || (data.error && !data.data)) {
            clearTimeout(handshakeTimeout);
            this.ws.removeEventListener('message', handshake);
            reject(new Error(data.error || 'WebSocket proxy rejected the connection'));
            return;
          }
          if (data.type !== 'ready') return;

          clearTimeout(handshakeTimeout);
          this.ws.removeEventListener('message', handshake);
          try {
            // Always subscribe from `desired`, so a reconnect restores the
            // subscription rather than leaving an open, silent socket.
            const target = this.desired || { deviceId, keys };
            this.subscribe(target.deviceId, target.keys);
            this.startStaleWatch();
            resolve();
          } catch (err) {
            reject(err);
          }
        };

        this.ws.onopen = () => {
          console.log('✅ WebSocket connected, authenticating');
          this.isConnecting = false;
          this.reconnectAttempt = 0;

          this.ws.addEventListener('message', handshake);
          try {
            this.ws.send(JSON.stringify({ type: 'auth', token }));
          } catch (err) {
            reject(err);
            return;
          }
          handshakeTimeout = setTimeout(() => {
            this.ws.removeEventListener('message', handshake);
            reject(new Error('Timed out waiting for the WebSocket proxy to become ready'));
          }, 15000);
        };

        this.ws.onmessage = (event) => {
          this.lastMessageMs = Date.now();
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (err) {
            console.error('❌ Failed to parse message:', event.data);
          }
        };
  
        this.ws.onerror = (err) => {
          console.error('❌ WebSocket error:', err);
          this.isConnecting = false;
          reject(err);
        };
  
        this.ws.onclose = (event) => {
          console.log('🔌 WebSocket closed:', event.code, event.reason);
          this.isConnecting = false;
          this.ws = null;
          this.stopStaleWatch();

          // Retry indefinitely with backoff. Giving up after a fixed number of
          // tries meant any outage longer than ~15s killed real-time until the
          // page was reloaded, with nothing on screen to say so.
          if (event.code === 1000 || !this.started) return;
          const delay = Math.min(10000, 1000 * 2 ** this.reconnectAttempt) + Math.floor(Math.random() * 400);
          this.reconnectAttempt += 1;
          console.log(`🔄 Reconnecting in ${delay}ms (attempt ${this.reconnectAttempt})`);
          setTimeout(() => {
            const target = this.desired || { deviceId, keys };
            this.connect(target.deviceId, target.keys).catch(console.error);
          }, delay);
        };
      });
    }
  
    /**
     * Subscribe to a device's latest telemetry. The proxy forwards this to
     * ThingsBoard verbatim, so it is ThingsBoard's own command format.
     */
    subscribe(deviceId, keys) {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        throw new Error('WebSocket not connected');
      }

      const cmdId = this.newCmdId();
      const subscriptionMessage = {
        cmds: [
          {
            entityType: 'DEVICE',
            entityId: deviceId,
            scope: 'LATEST_TELEMETRY',
            cmdId,
            type: 'TIMESERIES',
            ...(keys?.length ? { keys: keys.join(',') } : {})
          }
        ]
      };

      this.ws.send(JSON.stringify(subscriptionMessage));
      this.subscriptions.set(cmdId, { deviceId, keys });
      console.log(`📡 Telemetry subscription sent for ${deviceId} (${keys?.length || 0} keys)`);
    }

    /**
     * Handle incoming WebSocket messages
     */
    handleMessage(data) {
      // Ignore auth responses (handled separately) and heartbeats -- the
      // timestamp bookkeeping in onmessage is all a ping is for.
      if (data.authCmd) return;
      if (data.type === 'ping' || data.type === 'ready') return;
  
      if (data.errorCode) {
        console.error('❌ ThingsBoard error:', data);
        return;
      }
  
      if (data.data) {
        this.onMessageCallbacks.forEach(cb => {
          try {
            cb(data.data);
          } catch (err) {
            console.error('Telemetry callback error:', err);
          }
        });
      }
    }
  
    /**
     * Register telemetry callback
     */
    onTelemetryUpdate(callback) {
      this.onMessageCallbacks.push(callback);
    }
  
    /**
     * Remove telemetry callback
     */
    offTelemetryUpdate(callback) {
      this.onMessageCallbacks = this.onMessageCallbacks.filter(cb => cb !== callback);
    }
  
    /**
     * Disconnect WebSocket
     */
    disconnect() {
      // Deliberate teardown: clearing `started` is what stops onclose from
      // reconnecting. The old code exhausted the retry counter instead, which
      // also poisoned the next connect() -- after one disconnect, a dropped
      // socket would never come back for the life of the page.
      this.started = false;
      this.desired = null;
      this.stopStaleWatch();
      if (this.ws) {
        this.ws.close(1000, 'Client disconnect');
        this.ws = null;
      }
      this.subscriptions.clear();
      this.onMessageCallbacks = [];
      this.reconnectAttempt = 0;
    }
  
    /**
     * Check connection state
     */
    isConnected() {
      return this.ws && this.ws.readyState === WebSocket.OPEN;
    }
  }
  
  // Export singleton instance
  export default new ThingsBoardWebSocket();
  